import { formatFileSize } from '../../utils/fileHash';
import { LoadingSpinner } from '../ui';
import type { PendingFile, UploadResult } from './useUpload';
import { sortUploadResults } from './uploadResultsSort';

interface UploadPanelsProps {
  pendingFiles: PendingFile[];
  uploading: boolean;
  uploadProgress: {
    current: number;
    total: number;
    fileName: string;
    phase?: 'creating_folders' | 'uploading_files';
  };
  uploadResults: UploadResult[];
  // Results belong to the folder the upload was launched from; this is false once
  // the user navigates elsewhere, so the panel doesn't pop up on freshly-opened folders.
  resultsVisible: boolean;
  onRemovePendingFile: (id: string) => void;
  onClearPendingFiles: () => void;
  onUploadPendingFiles: () => void;
  onClearUploadResults: () => void;
}

export function UploadPanels({
  pendingFiles,
  uploading,
  uploadProgress,
  uploadResults,
  resultsVisible,
  onRemovePendingFile,
  onClearPendingFiles,
  onUploadPendingFiles,
  onClearUploadResults,
}: UploadPanelsProps) {
  return (
    <>
      {/* Pending files panel */}
      {pendingFiles.length > 0 && !uploading && (
        <div className="pending-files-panel">
          <div className="pending-header">
            <h4>
              Files ready to upload ({pendingFiles.filter((f) => !f.validationError).length})
              {pendingFiles.some((f) => f.relativePath) && ' from folder'}
            </h4>
            <div className="pending-actions">
              <button className="btn-secondary btn-small" onClick={onClearPendingFiles}>
                Clear All
              </button>
              <button
                className="btn-primary btn-small"
                onClick={onUploadPendingFiles}
                disabled={pendingFiles.every((f) => f.validationError)}
              >
                Upload All
              </button>
            </div>
          </div>
          <div className="pending-list">
            {pendingFiles.map((pending) => (
              <div
                key={pending.id}
                className={`pending-item ${pending.validationError ? 'pending-item-invalid' : ''}`}
              >
                <div className="pending-info">
                  <span className="pending-filename">{pending.file.name}</span>
                  <span className="pending-size">{formatFileSize(pending.file.size)}</span>
                  {pending.relativePath && (
                    <span className="pending-path">{pending.relativePath}</span>
                  )}
                  {pending.validationError && <span className="pending-error">{pending.validationError}</span>}
                </div>
                <button className="pending-remove" onClick={() => onRemovePendingFile(pending.id)} title="Remove">
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="upload-progress-panel">
          <LoadingSpinner
            message={
              uploadProgress.phase === 'creating_folders'
                ? `Creating folder: ${uploadProgress.fileName} (${uploadProgress.current}/${uploadProgress.total})`
                : `Uploading files… (${uploadProgress.current}/${uploadProgress.total})`
            }
          />
        </div>
      )}

      {/* Upload results */}
      {resultsVisible && (
        <div className="upload-results-panel">
          <div className="results-header">
            <h4>Upload Results</h4>
            <button className="results-close" onClick={onClearUploadResults} title="Dismiss">
              ×
            </button>
          </div>
          <div className="results-summary">
            {(() => {
              const uploaded = uploadResults.filter(r => r.status === 'uploaded').length;
              const folders = uploadResults.filter(r => r.status === 'folder_created').length;
              const renamed = uploadResults.filter(r => r.status === 'renamed').length;
              const skipped = uploadResults.filter(r => r.status === 'skipped').length;
              const foldersSkipped = uploadResults.filter(r => r.status === 'folder_skipped').length;
              const failed = uploadResults.filter(r => r.status === 'failed').length;
              // Lead with failures, then warnings, then successes \u2014 same priority as the
              // list below, so the counts the user most needs to see come first.
              return (
                <>
                  {failed > 0 && (
                    <span className="summary-badge summary-failed">
                      <span className="summary-icon">{'\u2717'}</span>
                      {failed} file{failed !== 1 ? 's' : ''} failed
                    </span>
                  )}
                  {skipped > 0 && (
                    <span className="summary-badge summary-skipped">
                      <span className="summary-icon">{'\u2298'}</span>
                      {skipped} file{skipped !== 1 ? 's' : ''} already existing
                    </span>
                  )}
                  {foldersSkipped > 0 && (
                    <span className="summary-badge summary-skipped">
                      <span className="summary-icon">{'\u2298'}</span>
                      {foldersSkipped} folder{foldersSkipped !== 1 ? 's' : ''} already existing
                    </span>
                  )}
                  {renamed > 0 && (
                    <span className="summary-badge summary-renamed">
                      <span className="summary-icon">{'\u21BB'}</span>
                      {renamed} file{renamed !== 1 ? 's' : ''} renamed
                    </span>
                  )}
                  {uploaded > 0 && (
                    <span className="summary-badge summary-uploaded">
                      <span className="summary-icon">{'\u2713'}</span>
                      {uploaded} file{uploaded !== 1 ? 's' : ''} uploaded
                    </span>
                  )}
                  {folders > 0 && (
                    <span className="summary-badge summary-folder_created">
                      <span className="summary-icon">{'\uD83D\uDCC1'}</span>
                      {folders} folder{folders !== 1 ? 's' : ''} created
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          <div className="results-list">
            {sortUploadResults(uploadResults).map((result, index) => (
              <div key={index} className={`result-item result-${result.status}`}>
                <span className="result-icon">
                  {result.status === 'uploaded' && '\u2713'}
                  {result.status === 'renamed' && '\u21BB'}
                  {(result.status === 'skipped' || result.status === 'folder_skipped') && '\u2298'}
                  {result.status === 'failed' && '\u2717'}
                  {result.status === 'folder_created' && '\uD83D\uDCC1'}
                </span>
                <span className="result-filename">{result.fileName}</span>
                <span className="result-message">
                  {result.status === 'uploaded' && 'Uploaded successfully'}
                  {result.status === 'renamed' && `Renamed to ${result.newName}`}
                  {(result.status === 'skipped' || result.status === 'folder_skipped') && result.message}
                  {result.status === 'failed' && (
                    <>
                      {result.message}
                      {result.retryable !== false && ' — please try uploading it again.'}
                    </>
                  )}
                  {result.status === 'folder_created' && result.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
